import { combineReducers } from 'redux';
import { GAME_ID, ROUND, SINGLE_PART_LEVELS } from 'a/pictureThis/data/constants';
import * as fromCurriculum from 'a/pictureThis/content/curriculum';
import view, * as fromView from './view';
import currentRound, * as fromCurrentRound from './currentRound';
import standardView, * as fromStandardView from './standardView';
import tutorState, * as fromTutorState from './tutorState';
import sessionData, * as fromSessionData from './sessionData';
import gamePanel, * as fromGamePanel from './gamePanel';

import { getIsQuestWorkflow } from '../../../hub/selectors';

export const createGameReducer = (gameReducer, id) => (state, action) => {
  if (id === action.gameId || state === undefined) {
    return gameReducer(state, action);
  }
  return state;
};

const pictureThisReducer = combineReducers({
  sessionData: createGameReducer(sessionData, GAME_ID),
  view: createGameReducer(view, GAME_ID),
  currentRound: createGameReducer(currentRound, GAME_ID),
  standardView: createGameReducer(standardView, GAME_ID),
  tutorState: createGameReducer(tutorState, GAME_ID),
  gamePanel: createGameReducer(gamePanel, GAME_ID),
});

export default pictureThisReducer;

// selectors from currentRound
export const getQuestionIndex = (state) => {
  const incorrect = selectors.getIncorrectAnswerCount(state);
  const correct = selectors.getCorrectAnswerCount(state);
  return incorrect + correct;
};

export const getStoryScript = state =>
  fromCurrentRound.getStoryScript(state.pictureThisReducer.currentRound);

export const getStoryScriptPage = state =>
  fromCurrentRound.getStoryScriptPage(state.pictureThisReducer.currentRound);

export const getContentIndex = state =>
  fromCurrentRound.getContentIndex(state.pictureThisReducer.currentRound);

export const getStoryId = state =>
  fromCurrentRound.getStoryId(state.pictureThisReducer.currentRound);

export const getPageIndex = state =>
  fromCurrentRound.getPageIndex(state.pictureThisReducer.currentRound);

export const getLevelIndex = state =>
  fromCurrentRound.getLevelIndex(state.pictureThisReducer.currentRound);

export const getCurrentLevelIndex = state =>
  fromCurrentRound.getCurrentLevelIndex(state.pictureThisReducer.currentRound);

export const getMaxPage = state =>
  fromCurrentRound.getMaxPage(state.pictureThisReducer.currentRound);

export const getIncorrectAnswerCount = state =>
  fromCurrentRound.getIncorrectAnswerCount(state.pictureThisReducer.currentRound);

export const getCorrectAnswerCount = state =>
  fromCurrentRound.getCorrectAnswerCount(state.pictureThisReducer.currentRound);

export const getHelpCount = state =>
  fromCurrentRound.getHelpCount(state.pictureThisReducer.currentRound);

export const getIsScaffold = state =>
  fromCurrentRound.getIsScaffold(state.pictureThisReducer.currentRound);

export const getIsInteractionLocked = state =>
  fromCurrentRound.getIsInteractionLocked(state.pictureThisReducer.currentRound);

export const getTotalStoriesAnswered = state =>
  fromCurrentRound.getTotalStoriesAnswered(state.pictureThisReducer.currentRound);

export const getTriesCount = state =>
  fromCurrentRound.getTriesCount(state.pictureThisReducer.currentRound);

export const getTriesCountOnCurrentElement = state =>
  fromCurrentRound.getTriesCountOnCurrentElement(state.pictureThisReducer.currentRound);

export const getLevelPercent = (state) => {
  if (selectors.getIsOnboarding(state)) {
    return 0;
  }
  return (selectors.getTotalStoriesAnswered(state) / ROUND.STORIES_PER_LEVEL) * 100;
};
// selectors from standardView
export const getCollectedElements = state =>
  fromStandardView.getCollectedElements(state.pictureThisReducer.standardView);

export const getGroupOffset = (state, id) =>
  fromStandardView.getGroupOffset(state.pictureThisReducer.standardView, id);

export const getIsGroupActive = (state, id) =>
  fromStandardView.getIsGroupActive(state.pictureThisReducer.standardView, id);

export const getGroupIsReturning = (state, id) =>
  fromStandardView.getGroupIsReturning(state.pictureThisReducer.standardView, id);

export const getIsGroupDragged = (state, id) =>
  fromStandardView.getIsGroupDragged(state.pictureThisReducer.standardView, id);

export const getGroupText = (state, id) =>
  fromStandardView.getGroupText(state.pictureThisReducer.standardView, id);

export const getGroupUuid = (state, id) =>
  fromStandardView.getGroupUuid(state.pictureThisReducer.standardView, id);

export const getIsUserDragging = state =>
  fromStandardView.getIsUserDragging(state.pictureThisReducer.standardView);

export const getAreWordsReturning = state =>
  fromStandardView.getAreWordsReturning(state.pictureThisReducer.standardView);

export const getIsTextDraggable = (state, groupId, element) => !selectors.getIsInteractionLocked(state)
  && selectors.getIsWordActive(state, groupId, element)
  && !selectors.getGroupIsReturning(state, groupId);

export const getIsScriptContainerHidden = state =>
  selectors.getIsUserDragging(state) || selectors.getAreWordsReturning(state);

export const getIsWordActive = (state, groupId, element) => {
  if (selectors.getIsShowingHint(state)) {
    return element === selectors.getDropzoneStoryTag(state) && selectors.getIsGroupActive(state, groupId);
  }
  return selectors.getIsGroupActive(state, groupId);
};

export const getIsWordHighlighted = (state, groupId, element) => {
  if (selectors.getIsShowingHint(state)) {
    return element === selectors.getDropzoneStoryTag(state) && selectors.getIsGroupActive(state, groupId);
  }
  return false;
};

export const getDraggablesTexts = state =>
  fromStandardView.getDraggablesTextsByPage(
    state.pictureThisReducer.standardView,
    selectors.getPageIndex(state),
  );

export const getCurrentDraggableGroupData = (state) => {
  const result = Object.values(state.pictureThisReducer.standardView.draggables.groupsById).find(el =>
    el.isBeingDragged);
  if (result) {
    return {
      groupId: result.groupId,
      element: result.element,
    };
  }
  return null;
};

export const getIsStoryDone = state =>
  fromStandardView.getIsStoryDone(state.pictureThisReducer.standardView);

export const getDropzoneLayout = state =>
  fromStandardView.getDropzoneLayout(state.pictureThisReducer.standardView);

export const getIsDropzoneTouching = state =>
  fromStandardView.getIsDropzoneTouching(state.pictureThisReducer.standardView);

const getUsedElements = state => state.pictureThisReducer.standardView.usedElements;

export const getUserFacingDropzoneStoryTag = state =>
  fromStandardView.getDropzoneStoryTag(state.pictureThisReducer.standardView);

// sometimes the "user facing" dropzone story tag will say something like "character 2."
// however, if the student has already used the text from curriculum that is marked "character 2,"
// then the game is really looking for "character 1" under-the-hood.
export const getDropzoneStoryTag = (state) => {
  let elementToHighlight = getUserFacingDropzoneStoryTag(state);

  if (getUsedElements(state).includes(elementToHighlight)) {
    if (elementToHighlight.includes('1')) {
      elementToHighlight = elementToHighlight.replace('1', '2');
    } else {
      elementToHighlight = elementToHighlight.replace('2', '1');
    }
  }

  return elementToHighlight;
};

export const getDropzoneShakeTrigger = state =>
  fromStandardView.getDropzoneShakeTrigger(state.pictureThisReducer.standardView);

export const getScrollPanelPosition = state =>
  fromStandardView.getScrollPanelPosition(state.pictureThisReducer.standardView);

export const getIsShowingImage = state =>
  fromStandardView.getIsShowingImage(state.pictureThisReducer.standardView);

export const getIsShowingPayoff = state =>
  fromStandardView.getIsShowingPayoff(state.pictureThisReducer.standardView);

// selectors from View
export const getScreen = state =>
  fromView.getScreen(state.pictureThisReducer.view);

// selector from tutorState
export const getTutorState = state =>
  fromTutorState.getTutorState(state.pictureThisReducer.tutorState);

export const getTutorStateCode = state =>
  fromTutorState.getTutorStateCode(state.pictureThisReducer.tutorState);

export const getTutorFeedbackTrigger = state =>
  fromTutorState.getTutorFeedbackTrigger(state.pictureThisReducer.tutorState);

export const getTutorOnboardingTrigger = state =>
  fromTutorState.getTutorOnboardingTrigger(state.pictureThisReducer.tutorState);

export const getStopTutorTrigger = state =>
  fromTutorState.getStopTutorTrigger(state.pictureThisReducer.tutorState);

export const getTutorScaffoldTrigger = state =>
  fromTutorState.getTutorScaffoldTrigger(state.pictureThisReducer.tutorState);

export const getTutorPromptTrigger = state =>
  fromTutorState.getTutorPromptTrigger(state.pictureThisReducer.tutorState);

// selectors from sessionData
export const getIsOnboardingReplay = state =>
  fromSessionData.getIsOnboardingReplay(state.pictureThisReducer.sessionData);

export const getIsOnboarding = (state) => {
  const { onboardingCompleted, isOnBoardingReplay } = state.pictureThisReducer.sessionData;
  const isLevelOnboardingComplete = selectors.getCurrentLevelIndex(state) > SINGLE_PART_LEVELS
    ?
    onboardingCompleted[1]
    :
    onboardingCompleted[0];
  return isOnBoardingReplay || !isLevelOnboardingComplete; // uncomplete onboarding for current level also flags onboarding
};

export const getLastLevelUnlocked = state =>
  fromSessionData.getLastLevelUnlocked(state.pictureThisReducer.sessionData);

export const getLevelsCompleted = state =>
  fromSessionData.getLevelsCompleted(state.pictureThisReducer.sessionData);

export const getLevelsCompletedCount = state =>
  fromSessionData.getLevelsCompletedCount(state.pictureThisReducer.sessionData);

export const getLevelsMastered = state =>
  fromSessionData.getLevelsMastered(state.pictureThisReducer.sessionData);

export const getIsLevelMastered = state =>
  fromSessionData.getIsLevelMastered(state.pictureThisReducer.sessionData, getLevelIndex(state));

// selectors from gamePanel
export const getIsShowingHint = state =>
  fromGamePanel.getIsShowingHint(state.pictureThisReducer.gamePanel);

export const getNextCardAnimationTrigger = state =>
  fromGamePanel.getNextCardAnimationTrigger(state.pictureThisReducer.gamePanel);

export const getIsArrowAnimating = (state, direction) => {
  const currentPage = selectors.getPageIndex(state);
  const targetPage = fromCurriculum.getTargetPageOfElement(
    selectors.getLevelIndex(state),
    selectors.getContentIndex(state),
    currentPage,
    selectors.getDropzoneStoryTag(state),
  );
  if (currentPage < targetPage && direction === 'right') {
    return selectors.getIsShowingHint(state);
  } if (targetPage < currentPage && direction === 'left') {
    return selectors.getIsShowingHint(state);
  }
  return false;
};

export const getIsDropzoneAnimating = (state) => {
  const currentPage = selectors.getPageIndex(state);
  const targetPage = fromCurriculum.getTargetPageOfElement(
    selectors.getLevelIndex(state),
    selectors.getContentIndex(state),
    currentPage,
    selectors.getDropzoneStoryTag(state),
  );
  return selectors.getIsShowingHint(state) && (currentPage === targetPage);
};

export const getIsFirstPage = state => selectors.getPageIndex(state) === 0;

export const getIsLastPage = state => selectors.getPageIndex(state) === selectors.getMaxPage(state) - 1;

export const getIsPageButtonActive = (state, direction) => {
  const isInactive = (direction === 'left' && selectors.getIsFirstPage(state)
  || direction === 'right' && selectors.getIsLastPage(state));
  return !isInactive;
};

export const getTargetAction = (state) => {
  const targetStoryPart = getDropzoneStoryTag(state);
  const script = getStoryScript(state);
  let targetActionPosition = 0;
  for (let i = 0; i < script.length; i++) {
    for (let j = 0; j < script[i].length; j++) {
      if (script[i][j].element === targetStoryPart) {
        return {
          targetAction: script[i][j].text,
          targetActionPosition,
        };
      }
      targetActionPosition++;
    }
  }
  return { targetAction: 'NOT_FOUND', targetActionPosition: 'NOT_FOUND' };
};

export const getContentVersion = () =>
  fromCurriculum.getContentVersion();

export const selectors = {
  getQuestionIndex,
  getStoryScript,
  getStoryScriptPage,
  getContentIndex,
  getStoryId,
  getPageIndex,
  getLevelIndex,
  getCurrentLevelIndex,
  getMaxPage,
  getIncorrectAnswerCount,
  getCorrectAnswerCount,
  getHelpCount,
  getIsScaffold,
  getIsInteractionLocked,
  getIsQuestWorkflow,
  getTotalStoriesAnswered,
  getTriesCount,
  getTriesCountOnCurrentElement,
  getLevelPercent,
  getCollectedElements,
  getGroupOffset,
  getIsGroupActive,
  getGroupIsReturning,
  getIsGroupDragged,
  getGroupText,
  getGroupUuid,
  getIsUserDragging,
  getAreWordsReturning,
  getIsTextDraggable,
  getIsScriptContainerHidden,
  getIsWordActive,
  getIsWordHighlighted,
  getDraggablesTexts,
  getIsStoryDone,
  getDropzoneLayout,
  getIsDropzoneTouching,
  getUserFacingDropzoneStoryTag,
  getDropzoneStoryTag,
  getDropzoneShakeTrigger,
  getScrollPanelPosition,
  getIsShowingImage,
  getIsShowingPayoff,
  getScreen,
  getTutorState,
  getTutorStateCode,
  getTutorFeedbackTrigger,
  getTutorOnboardingTrigger,
  getStopTutorTrigger,
  getTutorScaffoldTrigger,
  getTutorPromptTrigger,
  getIsOnboardingReplay,
  getIsOnboarding,
  getLastLevelUnlocked,
  getLevelsCompleted,
  getLevelsCompletedCount,
  getLevelsMastered,
  getIsLevelMastered,
  getIsShowingHint,
  getNextCardAnimationTrigger,
  getIsArrowAnimating,
  getIsDropzoneAnimating,
  getIsFirstPage,
  getIsLastPage,
  getIsPageButtonActive,
  getTargetAction,
  getContentVersion,
  getCurrentDraggableGroupData,
};
